import { useState, RefObject, useCallback } from 'react';
import { useDebounceFn } from 'ahooks';
import { useIntl } from 'umi';
import { notification } from 'tea-component';
import { SmartTableActions } from '../../ChatDSTemplate/type';
import { downloadExcel } from '@/pages/dashboard/utils/download';
import { useConfirmDelete } from '../components/ExcelSheetTabs/hooks/useConfirmDelete';
import type { ISmartTableGridRef, IColumnConfig, TTableRow } from '../components/SmartTableGrid';
import { downloadCsv, copyTableToClipboard, multiSheetDataToExcelFile, IMultiSheetData, trimSheetDataToEffectiveRange } from '../utils';
import { DEFAULT_EMPTY_ROW_COUNT, DEFAULT_EMPTY_COL_COUNT, SmartTableExportType } from '../constants';
import type { TSheetDataMap } from '../hooks/useSheetData';
import type { ISheet } from '../components/ExcelSheetTabs';
import useCos from '../../components/QAInput/hooks/useCOS';

interface IUseSmartTableActionsProps {
  /** 表格组件 ref */
  gridRef?: RefObject<ISmartTableGridRef>;
  /** 获取表格数据的回调（可选，优先使用 gridRef） */
  getTableData?: () => string;
  /** 当前 sheet 名称（用于导出文件名） */
  sheetName?: string;
  /** 重置回调（由外部传入，用于获取原始文件数据） */
  onReset?: () => Promise<void>;
  /** 当前文件名（用于保存时上传） */
  currentFileName?: string;
  /** 当前文件的 COS 路径（后端返回，用于保存时上传到正确路径） */
  currentFileCosUrl?: string;
  /** 所有 sheet 数据 Map（用于多 sheet 保存） */
  sheetDataMap?: TSheetDataMap;
  /** 所有 sheet 信息列表（用于获取 sheet 名称和顺序） */
  sheets?: ISheet[];
  /** 保存成功后的回调（用于刷新 Excel 数据） */
  onSaveSuccess?: () => Promise<void> | void;
}

/**
 * 智能表格操作 Hook
 * 使用 map 映射模式处理不同操作事件
 * 状态内聚：重置、保存、导出等操作在内部处理
 */
export const useSmartTableActions = (props: IUseSmartTableActionsProps) => {
  const { gridRef, sheetName = 'Sheet1', onReset, onSaveSuccess, currentFileName = '', currentFileCosUrl = '', sheetDataMap, sheets = [] } = props;
  const { formatMessage } = useIntl();
  const [handleConfirmDelete] = useConfirmDelete();
  
  // 复制提示状态
  const [showCopyTips, setShowCopyTips] = useState(false);
  // 保存加载状态
  const [isSaving, setIsSaving] = useState(false);

  // COS 上传 hook
  const { putObject } = useCos();

  /**
   * 判断单元格值是否为空
   */
  const isCellEmpty = useCallback((value: any): boolean => value === null || value === undefined || value === '', []);

  /**
   * 计算有效数据范围（最后一个有数据的行和列）
   * 用于导出/复制时裁剪空白区域，优化性能
   */
  const getDataBoundary = useCallback((data: TTableRow[], columns: IColumnConfig[]): { lastRow: number; lastCol: number; } => {
    let lastRow = -1;
    let lastCol = -1;

    // 遍历所有单元格，找到最后一个有数据的位置
    for (let rowIdx = 0;rowIdx < data.length;rowIdx++) {
      const row = data[rowIdx];
      for (let colIdx = 0;colIdx < columns.length;colIdx++) {
        const value = row[columns[colIdx].data];
        if (!isCellEmpty(value)) {
          // 更新最后有数据的行和列
          if (rowIdx > lastRow) lastRow = rowIdx;
          if (colIdx > lastCol) lastCol = colIdx;
        }
      }
    }

    return { lastRow, lastCol };
  }, [isCellEmpty]);

  /**
   * 将表格数据转换为二维数组格式（用于复制）
   * 优化：只包含有数据的范围，不复制空白列和空白行
   * 始终以当前表格第一行数据作为表头，数据从第二行开始
   */
  const getTableDataAsArray = useCallback((): string[][] => {
    const data = gridRef?.current?.getData() || [];
    const columns = gridRef?.current?.getColumns() || [];
    
    if (data.length === 0 || columns.length === 0) {
      return [];
    }

    // 计算有效数据范围
    const { lastRow, lastCol } = getDataBoundary(data, columns);
    
    // 如果没有任何数据，返回空数组
    if (lastRow < 0 || lastCol < 0) {
      return [];
    }

    // Only trim when data boundary exceeds the default blank size;
    // otherwise keep the original size (which is at least the default).
    const effectiveLastCol = Math.max(lastCol, Math.min(DEFAULT_EMPTY_COL_COUNT, columns.length) - 1);
    const effectiveLastRow = Math.max(lastRow, Math.min(DEFAULT_EMPTY_ROW_COUNT, data.length) - 1);

    // 只取有效范围的列
    const effectiveColumns = columns.slice(0, effectiveLastCol + 1);
    
    // 只取有效范围的行
    const effectiveData = data.slice(0, effectiveLastRow + 1);
    
    if (effectiveData.length === 0) {
      return [];
    }

    // Always use the first row of tableData as headers
    const firstRow = effectiveData[0];
    const headers = effectiveColumns.map((col) => {
      const value = firstRow[col.data];
      return value !== null && value !== undefined ? String(value) : '';
    });
    // Data starts from the second row
    const remainingData = effectiveData.slice(1);
    const rows = remainingData.map((row) => effectiveColumns.map((col) => {
      const value = row[col.data];
      return value !== null && value !== undefined ? String(value) : '';
    }));
    return [headers, ...rows];
    
  }, [gridRef, getDataBoundary]);

  // 内部保存方法 - 将所有 sheet 数据转换为 Excel 文件并上传到 COS
  const handleSave = async () => {
    // 收集所有 sheet 的数据（包括当前编辑的 sheet）
    const allSheetsData: IMultiSheetData[] = [];
    
    // 获取当前 sheet 的最新数据（从 gridRef）
    const currentData = gridRef?.current?.getData() || [];
    const currentColumns = gridRef?.current?.getColumns() || [];
    
    // Save ALL sheets as-is, no filtering
    if (sheetDataMap && sheets.length > 0) {
      sheets.forEach((sheet) => {
        const sheetData = sheetDataMap.get(sheet.id);
        allSheetsData.push({
          sheetName: sheet.name,
          tableData: sheetData?.tableData || [],
          columns: sheetData?.columns || [],
        });
      });
    } else {
      // Single sheet mode
      allSheetsData.push({
        sheetName,
        tableData: currentData,
        columns: currentColumns,
      });
    }
    
    // 检查是否有数据
    if (allSheetsData.length === 0) {
      return false;
    }

    // 获取文件名：优先使用当前文件名，否则使用 sheetName
    const fileName = currentFileName || `${sheetName}.xlsx`;
    // 确保文件名以 .xlsx 结尾
    const finalFileName = fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`;
    
    setIsSaving(true);
    
    try {
      // 1. 将所有 sheet 数据转换为 Excel File 对象
      const excelFile = await multiSheetDataToExcelFile(allSheetsData, finalFileName);

      // 2. 上传到 COS（从完整 COS URL 中提取域名后的路径部分）
      const filePath = currentFileCosUrl?.replace(/^https?:\/\/[^/]+/, '') || `/databrain_agent/test/user_file/${finalFileName}`;
      
      await putObject({
        path: filePath,
        file: excelFile,
      });
      
      setIsSaving(false);
      return true;
    } catch (error) {
      console.error('保存失败:', error);
      setIsSaving(false);
      throw error;
    }
  };

  /**
   * 裁剪数据到有效范围（只保留有数据的行和列）
   * 用于导出时优化性能，避免导出大量空白单元格
   * Note: only trim when data exceeds the default blank size;
   * if data is smaller than the default, keep the default size.
  //  */
  // const trimDataToEffectiveRange = useCallback((data: TTableRow[], columns: IColumnConfig[]): { trimmedData: TTableRow[]; trimmedColumns: IColumnConfig[]; } => {
  //   const { lastRow, lastCol } = getDataBoundary(data, columns);
    
  //   // 如果没有任何数据，返回空
  //   if (lastRow < 0 || lastCol < 0) {
  //     return { trimmedData: [], trimmedColumns: [] };
  //   }

  //   // Only trim when data boundary exceeds the default blank size;
  //   // otherwise keep the original size (which is at least the default).
  //   const effectiveLastCol = Math.max(lastCol, Math.min(DEFAULT_EMPTY_COL_COUNT, columns.length) - 1);
  //   const effectiveLastRow = Math.max(lastRow, Math.min(DEFAULT_EMPTY_ROW_COUNT, data.length) - 1);

  //   // 裁剪列配置
  //   const trimmedColumns = columns.slice(0, effectiveLastCol + 1);
  //   // 裁剪数据行
  //   const trimmedData = data.slice(0, effectiveLastRow + 1);

  //   return { trimmedData, trimmedColumns };
  // }, [getDataBoundary]);

  /**
   * Build export-ready data for a single sheet:
   * 1. Trim effective data range (remove trailing empty rows and columns)
   * 2. Use first row as headers, data from second row.
   */
  const buildSheetExportData = useCallback((data: TTableRow[], columns: IColumnConfig[]) => {
    // 先裁剪有效数据范围，移除尾部空白行和空白列
    const { tableData: trimmedData, columns: trimmedColumns } = trimSheetDataToEffectiveRange({
      tableData: data,
      columns,
    });

    if (trimmedData.length === 0 || trimmedColumns.length === 0) {
      // Return empty but valid structure so the sheet is still included
      return { exportData: [] as TTableRow[], downloadColumns: [] as { code: string; name: string; width: number; }[] };
    }
    // Always use the first row of tableData as export headers
    const firstRow = trimmedData[0];
    const downloadColumns = trimmedColumns.map(col => ({
      code: col.data,
      name: firstRow[col.data] !== null && firstRow[col.data] !== undefined ? String(firstRow[col.data]) : '',
      width: col.width || 150,
    }));
    // Data starts from the second row
    const exportData = trimmedData.slice(1);
    return { exportData, downloadColumns };
  }, []);

  // Execute export: Excel exports ALL sheets, CSV exports current sheet only
  const executeExport = async (type: string, currentData: TTableRow[], currentColumns: IColumnConfig[]) => {
    if (type === SmartTableExportType.Excel) {
      // Build tabs for all sheets
      const tabs: { tabName: string; dataSource: TTableRow[]; columns: { code: string; name: string; width: number; }[]; }[] = [];

      if (sheetDataMap && sheets.length > 0) {
        // Export ALL sheets as-is, no filtering
        sheets.forEach((sheet) => {
          const sheetData = sheetDataMap.get(sheet.id);
          const result = sheetData
            ? buildSheetExportData(sheetData.tableData, sheetData.columns)
            : { exportData: [], downloadColumns: [] };
          tabs.push({
            tabName: sheet.name,
            dataSource: result.exportData,
            columns: result.downloadColumns,
          });
        });
      }

      // Fallback: if no multi-sheet data, use current sheet
      if (tabs.length === 0) {
        const result = buildSheetExportData(currentData, currentColumns);
        if (!result) {
          console.warn('没有有效数据可导出');
          return;
        }
        tabs.push({
          tabName: sheetName,
          dataSource: result.exportData,
          columns: result.downloadColumns,
        });
      }

      // Export file name: use currentFileName or sheetName
      const exportFileName = currentFileName?.replace(/\.(xlsx|xls|csv)$/i, '') || sheetName;

      await downloadExcel({
        fileName: exportFileName,
        tabs,
      });
    } else if (type === SmartTableExportType.CSV) {
      // CSV does not support multi-sheet, export current sheet only
      const result = buildSheetExportData(currentData, currentColumns);
      if (!result) {
        console.warn('没有有效数据可导出');
        return;
      }
      // CSV 文件名格式: 表格文件名_sheet名（去掉文件扩展名）
      const baseFileName = currentFileName?.replace(/\.(xlsx|xls|csv)$/i, '') || sheetName;
      const csvFileName = `${baseFileName}_${sheetName}`;
      downloadCsv({
        fileName: csvFileName,
        dataSource: result.exportData,
        columns: result.downloadColumns,
      });
    }
  };

  // 内部导出方法
  const handleExportData = async (type: string) => {
    const data = gridRef?.current?.getData() || [];
    const columns = gridRef?.current?.getColumns() || [];
    
    if (data.length === 0) {
      return;
    }
    
    try {
      // 直接执行导出（敏感权限场景在 UI 层已禁用导出按钮）
      await executeExport(type, data, columns);
      notification.success({
        description: formatMessage({ id: 'dba_smart_table_export_success' }),
        duration: 2000,
      });
    } catch (error) {
      console.error('导出失败:', error);
      notification.error({
        description: formatMessage({ id: 'dba_smart_table_export_failed' }),
        duration: 2000,
      });
    }
  };

  const { run: handleAction } = useDebounceFn(
    async ({ key }: { key: string; }) => {
      const actionsHandlers: Partial<Record<SmartTableActions, () => void | Promise<void>>> = {
        [SmartTableActions.Reset]: async () => {
          // 使用确认弹窗
          if (!onReset) {
            console.warn('未提供重置回调');
            return;
          }
          await handleConfirmDelete({
            mutate: async () => {
              await onReset();
            },
            caption: formatMessage({ id: 'dba_smart_table_reset_title' }),
            message: formatMessage({ id: 'dba_smart_table_reset_message' }),
            okText: formatMessage({ id: 'dba_confirm' }),
            cancelText: formatMessage({ id: 'dba_cancel' }),
            showNotification: true,
            successMessage: formatMessage({ id: 'dba_smart_table_reset_success' }),
          });
        },

        [SmartTableActions.Copy]: async () => {
          // 将表格数据转换为二维数组格式，支持复制到 Excel 和 Word
          const tableData = getTableDataAsArray();
          if (tableData.length === 0) {
            console.warn('表格数据为空');
            return;
          }
          const copyStatus = await copyTableToClipboard(tableData);
          // 防止重复显示提示
          if (showCopyTips) return;
          if (copyStatus) {
            setShowCopyTips(true);
            const timer = setTimeout(() => {
              setShowCopyTips(false);
              clearTimeout(timer);
            }, 2000);
          }
        },

        [SmartTableActions.Save]: async () => {
          try {
            const success = await handleSave();
            if (success) {
              notification.success({
                description: formatMessage({ id: 'dba_smart_table_save_success' }),
                duration: 2000,
              });
              // 保存成功后刷新 Excel 数据
              await onSaveSuccess?.();
            }
          } catch (error) {
            notification.error({
              description: formatMessage({ id: 'dba_smart_table_save_failed' }),
              duration: 2000,
            });
          }
        },
      };

      const handler = actionsHandlers[key as SmartTableActions];
      if (handler) {
        await handler();
      }
    },
    { wait: 300, leading: true }
  );

  // 导出操作（由下拉菜单触发）
  const { run: handleExport } = useDebounceFn(
    async (type: string) => {
      await handleExportData(type);
    },
    { wait: 300, leading: true }
  );

  return { handleAction, showCopyTips, handleExport, isSaving };
};
